// The email verification link carries the person's user name, confirmation
// code and email address in its query string. Those must not sit in the
// address bar: the analytics and advertising tags report the page address on
// their own every time the app moves to another page, and they report the
// address just left as where the visitor came from.
//
// So the query string is taken out of the address bar as soon as the app
// starts (see emailPrefillBootstrap.ts) and kept here for the verification
// page to read. It is kept for the life of the tab, not only in memory, so that
// reloading the verification page still verifies (a phone's browser reloads a
// tab it had put aside).
//
// This module imports only the address rule the tags use, because it runs
// before the rest of the app loads.
import { reportableSearch } from 'Utils/AnalyticsPageAddress';

const VERIFICATION_PATH = '/verify';
const VERIFICATION_LINK_STORAGE_KEY = 'museflow:verification-link';
const VERIFICATION_PARAMS: readonly string[] = ['user_name', 'confirmation_code', 'email'];

let heldSearch: string | undefined;

const isVerificationPath = (pathname: string): boolean =>
  pathname.replace(/\/+$/, '').toLowerCase() === VERIFICATION_PATH;

const holdsVerificationValues = (search: string): boolean => {
  const params = new URLSearchParams(search);
  return VERIFICATION_PARAMS.some((name) => params.has(name));
};

/** Keep a verification link's query string for the verification page. */
export const holdVerificationLinkSearch = (search: string): void => {
  heldSearch = search;
  try {
    sessionStorage.setItem(VERIFICATION_LINK_STORAGE_KEY, search);
  } catch {
    // sessionStorage may be unavailable in some contexts; memory still holds it.
  }
};

// Was this page brought back by the browser (a reload, or a tab restored or
// stepped back to) rather than opened afresh?
const pageWasBroughtBack = (): boolean => {
  try {
    const [navigation] = performance.getEntriesByType(
      'navigation',
    ) as PerformanceNavigationTiming[];
    return navigation?.type === 'reload' || navigation?.type === 'back_forward';
  } catch {
    // No navigation timing here: treat the page as opened afresh.
    return false;
  }
};

const storedSearch = (): string | undefined => {
  try {
    return sessionStorage.getItem(VERIFICATION_LINK_STORAGE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
};

/**
 * The held query string ('?user_name=...') for the verification page, handed
 * over once. A link held by this page load comes from memory and is then
 * dropped from it. The copy kept for the tab is used only when the browser
 * brought the page back, so a link from earlier in the tab is not verified
 * again by opening the page afresh.
 */
export const takeVerificationLinkSearch = (): string | undefined => {
  const held = heldSearch;
  heldSearch = undefined;
  if (held !== undefined) return held;
  return pageWasBroughtBack() ? storedSearch() : undefined;
};

/**
 * For the app's link handler: hold a verification link that opened the running
 * app, and give back the address to move to - the link's path with only its
 * campaign parameters.
 */
export const holdVerificationLinkFromAppUrl = (link: URL): string => {
  holdVerificationLinkSearch(link.search);
  return `${link.pathname}${reportableSearch(link.search)}`;
};

export const clearVerificationLinkSearch = (): void => {
  heldSearch = undefined;
  try {
    sessionStorage.removeItem(VERIFICATION_LINK_STORAGE_KEY);
  } catch {
    // sessionStorage may be unavailable in some contexts.
  }
};

/**
 * On /verify, move the link's query string out of the address bar and hold it.
 * Campaign parameters stay in the address; every other page is left alone.
 */
export const captureVerificationLinkFromUrl = (): void => {
  try {
    const url = new URL(window.location.href);
    if (!isVerificationPath(url.pathname) || !holdsVerificationValues(url.search)) return;
    holdVerificationLinkSearch(url.search);
    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${reportableSearch(url.search)}${url.hash}`,
    );
  } catch {
    // Never let this stop the app from starting: the page then reads the address as before.
  }
};
